import { createApi } from '@reduxjs/toolkit/query/react';
import isAfter from 'date-fns/isAfter';

import config from 'config';
import axiosBaseQuery from './axiosBaseQuery';
import generateBasicEndpoints from './generateBasicEndpoints';
import auth from './auth';
import problem from './problem';
import problemSet from './problemSet';
import optimization from './optimization';
import result from './result';
import inputServices from './inputServices';
import solverVersionServices from './solverVersionServices';

const { baseURL } = config;

const categoryEndpoints = generateBasicEndpoints(
  [
    '/home/categories',
    '/home/updateCategory',
    '/home/deleteCategory',
    '/home/addCategory',
  ],
  'Category'
);
const solverEndpoints = generateBasicEndpoints(
  [
    '/home/solvers',
    '/home/updateSolver',
    '/home/deleteSolver',
    '/home/addSolver',
  ],
  'Solver'
);
const itemEndpoints = generateBasicEndpoints(
  ['/home/items', '/home/updateItem', '/home/deleteItem', '/home/addItem'],
  'Item'
);

export const coapApi = createApi({
  reducerPath: 'coapApi',
  baseQuery: axiosBaseQuery({ baseURL }),
  tagTypes: [
    'Solver',
    'Category',
    'Item',
    'Jobs',
    'Problem',
    'Problem Set',
    'Optimization',
    'Result',
    'Auth',
    'Input',
    'SolverVersion',
  ],
  endpoints: (builder) => ({
    // category
    getCategories: builder.query(categoryEndpoints.queryList),
    updateCategory: builder.mutation(categoryEndpoints.update),
    deleteCategories: builder.mutation(categoryEndpoints.batchDelete),
    addCategory: builder.mutation(categoryEndpoints.add),

    // solver
    getSolvers: builder.query(solverEndpoints.queryList),
    updateSolver: builder.mutation(solverEndpoints.update),
    deleteSolvers: builder.mutation(solverEndpoints.batchDelete),
    addSolver: builder.mutation(solverEndpoints.add),

    // item
    getItems: builder.query(itemEndpoints.queryList),
    updateItem: builder.mutation(itemEndpoints.update),
    deleteItems: builder.mutation(itemEndpoints.batchDelete),
    addItem: builder.mutation(itemEndpoints.add),

    // input
    getInputs: builder.query(inputServices.queryList),

    // solver version
    getSolverVersion: builder.query(solverVersionServices.query),
    getSolverVersions: builder.query(solverVersionServices.queryList),

    // job
    listJobs: builder.query({
      query: (name) => ({
        url: 'optimization/squeue',
        method: 'GET',
      }),
      providesTags: (result, error, page) =>
        result?.squeueResultVOList
          ? [
              ...result?.squeueResultVOList.map(({ JOBNUMBER }) => ({
                type: 'Jobs',
                id: JOBNUMBER,
              })),
              { type: 'Jobs', id: 'LIST' },
            ]
          : [{ type: 'Jobs', id: 'LIST' }],
    }),
    getJob: builder.query({
      query: (data) => ({
        url: `optimization/visitor/result`,
        method: 'POST',
        data,
      }),
      providesTags: (result, error, data) => [
        { type: 'Jobs', id: data?.jobId },
      ],
    }),
    killJob: builder.mutation({
      query(data) {
        return {
          url: `optimization/visitor/cancel`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: (result, error, data) => [
        { type: 'Jobs', id: data?.jobId },
        { type: 'Jobs', id: 'LIST' },
      ],
    }),
    addJob: builder.mutation({
      query(data) {
        return {
          url: `optimization/visitor/base_problem`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: [{ type: 'Jobs', id: 'LIST' }],
    }),

    // statistics
    getDailyCount: builder.query({
      query: (data) => ({
        url: `statistic/days`,
        method: 'POST',
        data,
      }),
    }),
    getJobStatistics: builder.query({
      query: (data) => ({
        url: `statistic/dimension`,
        method: 'POST',
        data,
      }),
    }),
    getJobStatus: builder.query({
      query: (data) => ({
        url: `statistic/state`,
        method: 'POST',
        data,
      }),
    }),

    // common problem
    getCommonProblems: builder.query({
      query: (data) => ({
        url: '/problem/common-problems',
        method: 'POST',
        data,
      }),
    }),

    // competitions
    getCompetitions: builder.query({
      query: (data) => ({
        baseURL: '',
        url: '/competitions/list.json',
        method: 'GET',
      }),
      transformResponse: (response, meta, arg) => {
        const now = new Date();
        const avaliableList = response?.list?.filter(
          (item) => !item?.startDate || isAfter(now, item?.startDate)
        ) || [];
        return {
          list: avaliableList,
          total: avaliableList?.length
        }
      },
    }),
    // competitions
    getCompetition: builder.query({
      query: (id) => ({
        baseURL: '',
        url: `/competitions/${id}/detail.json`,
        method: 'GET',
      }),
    }),

    ...auth(builder),
    ...problem(builder),
    ...problemSet(builder),
    ...optimization(builder),
    ...result(builder),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  // category
  useGetCategoriesQuery,
  useUpdateCategoryMutation,
  useDeleteCategoriesMutation,
  useAddCategoryMutation,
  // solver
  useGetSolversQuery,
  useUpdateSolverMutation,
  useDeleteSolversMutation,
  useAddSolverMutation,
  // item
  useGetItemsQuery,
  useUpdateItemMutation,
  useDeleteItemsMutation,
  useAddItemMutation,
  // job
  useListJobsQuery,
  useGetJobQuery,
  useKillJobMutation,
  useAddJobMutation,
  // statistics
  useGetDailyCountQuery,
  useGetJobStatisticsQuery,
  useGetJobStatusQuery,
  // common problem
  useGetCommonProblemsQuery,
  // competitions
  useGetCompetitionsQuery,
  useGetCompetitionQuery,
} = coapApi;
